import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../../utils/api';

const API_BASE_URL = getApiBaseUrl();

const CourseForm = ({ course, user, onClose, onSubmit }) => {
  const [title, setTitle] = useState(course?.title || "");
  const [description, setDescription] = useState(course?.description || "");
  const [backgroundImage, setBackgroundImage] = useState(course?.background_image || "");
  const [sortOrder, setSortOrder] = useState(course?.sort_order || ""); // Campo de ordenação
  const [videos, setVideos] = useState(course?.videos || []);
  const [quizQuestions, setQuizQuestions] = useState(course?.quiz_questions || []);
  const [hasQuizCertificate, setHasQuizCertificate] = useState(
    course?.quiz_questions && course.quiz_questions.length > 0
  );
  const [isPublic, setIsPublic] = useState(
    course?.is_public !== undefined ? course.is_public : true
  );
  const [groupId, setGroupId] = useState(course?.group_id || "");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch groups when component mounts
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/groups?user_id=${user.id}`);
        const data = await response.json();
        setGroups(data.groups || []);
      } catch (error) {
        console.error("Error fetching groups:", error);
      }
    };
    fetchGroups();
  }, [user.id]);

  const addVideo = () => {
    setVideos([...videos, { id: Date.now().toString(), title: "", vimeo_url: "" }]);
  };

  const updateVideo = (index, field, value) => {
    const newVideos = [...videos];
    newVideos[index][field] = value;
    setVideos(newVideos);
  };

  const removeVideo = (index) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  const addQuizQuestion = () => {
    setQuizQuestions([
      ...quizQuestions,
      {
        question: "",
        options: ["", ""], // Começa com 2 opções
        correct_answer: 0,
      },
    ]);
  };

  const addQuizOption = (qIndex) => {
    const newQuestions = [...quizQuestions];
    newQuestions[qIndex].options.push("");
    setQuizQuestions(newQuestions);
  };

  const removeQuizOption = (qIndex, oIndex) => {
    const newQuestions = [...quizQuestions];
    if (newQuestions[qIndex].options.length > 2) {
      newQuestions[qIndex].options.splice(oIndex, 1);
      // Ajusta a resposta correta se necessário
      if (newQuestions[qIndex].correct_answer >= newQuestions[qIndex].options.length) {
        newQuestions[qIndex].correct_answer = 0;
      }
      setQuizQuestions(newQuestions);
    }
  };

  const updateQuizQuestion = (index, field, value) => {
    const newQuestions = [...quizQuestions];
    newQuestions[index][field] = value;
    setQuizQuestions(newQuestions);
  };

  const updateQuizOption = (qIndex, oIndex, value) => {
    const newQuestions = [...quizQuestions];
    newQuestions[qIndex].options[oIndex] = value;
    setQuizQuestions(newQuestions);
  };

  const removeQuizQuestion = (index) => {
    setQuizQuestions(quizQuestions.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return; // Prevent multiple submissions

    setLoading(true);

    const courseData = {
      title,
      description,
      background_image: backgroundImage || null,
      sort_order: sortOrder ? parseInt(sortOrder) : null, // Campo de ordenação
      videos,
      quiz_questions: hasQuizCertificate ? quizQuestions : [],
      is_public: isPublic,
      group_id: !isPublic ? groupId : null,
      user_id: user.id,
    };

    try {
      await onSubmit(courseData);
      alert("Curso salvo com sucesso!");
    } catch (error) {
      console.error("Error saving course:", error);
      alert("Erro ao salvar curso. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 p-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              {course ? "Editar Curso" : "Criar Novo Curso"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <svg
                className="w-6 h-6 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título do Curso
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Digite o título do curso"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imagem de Fundo (URL)
                </label>
                <input
                  type="url"
                  value={backgroundImage}
                  onChange={(e) => setBackgroundImage(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="https://exemplo.com/imagem.jpg"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ordem de Exibição
                  <span className="text-gray-500 text-xs block mt-1">
                    Deixe em branco para ordenação automática (mais recente primeiro)
                  </span>
                </label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="1, 2, 3..."
                  min="1"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                rows="3"
                placeholder="Digite a descrição do curso"
                required
              />
            </div>

            {/* Access Control Section */}
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Controle de Acesso</h3>

              <div className="space-y-4">
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="access"
                      checked={isPublic}
                      onChange={() => {
                        setIsPublic(true);
                        setGroupId("");
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Público - Visível para todos os usuários
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="access"
                      checked={!isPublic}
                      onChange={() => setIsPublic(false)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Privado - Apenas usuários de um grupo específico
                    </span>
                  </label>
                </div>

                {!isPublic && (
                  <div className="ml-7">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Selecione o Grupo
                    </label>
                    <select
                      value={groupId}
                      onChange={(e) => setGroupId(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      required={!isPublic}
                    >
                      <option value="">Selecione um grupo...</option>
                      {groups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name} ({group.users ? group.users.length : 0} usuários)
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={hasQuizCertificate}
                  onChange={(e) => setHasQuizCertificate(e.target.checked)}
                  className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Este curso possui teste final e certificado
                </span>
              </label>
              <p className="text-sm text-gray-500 mt-1 ml-8">
                Marque esta opção se o curso deve ter um teste final e gerar certificado ao
                completar
              </p>
            </div>

            {backgroundImage && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview da Imagem
                </label>
                <div
                  className="h-32 bg-cover bg-center rounded-xl border border-gray-200 overflow-hidden"
                  style={{ backgroundImage: `url(${backgroundImage})` }}
                >
                  <div className="h-full bg-gradient-to-t from-black/60 to-transparent flex items-end">
                    <div className="p-4">
                      <p className="text-white text-lg font-semibold">
                        {title || "Título do Curso"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Vídeos</h3>
                <button
                  type="button"
                  onClick={addVideo}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <span>Adicionar Vídeo</span>
                </button>
              </div>

              <div className="space-y-4">
                {videos.map((video, index) => (
                  <div key={video.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-medium text-gray-900">Vídeo {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeVideo(index)}
                        className="text-red-500 hover:text-red-700 transition-colors duration-200"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Título do Vídeo
                        </label>
                        <input
                          type="text"
                          value={video.title}
                          onChange={(e) => updateVideo(index, "title", e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          placeholder="Título do vídeo"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          URL do Vimeo
                        </label>
                        <input
                          type="url"
                          value={video.vimeo_url}
                          onChange={(e) => updateVideo(index, "vimeo_url", e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          placeholder="https://vimeo.com/123456789"
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {hasQuizCertificate && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Questões do Teste</h3>
                  <button
                    type="button"
                    onClick={addQuizQuestion}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    <span>Adicionar Questão</span>
                  </button>
                </div>

                <div className="space-y-6">
                  {quizQuestions.map((question, qIndex) => (
                    <div key={qIndex} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="font-medium text-gray-900">Questão {qIndex + 1}</h4>
                        <button
                          type="button"
                          onClick={() => removeQuizQuestion(qIndex)}
                          className="text-red-500 hover:text-red-700 transition-colors duration-200"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pergunta
                        </label>
                        <input
                          type="text"
                          value={question.question}
                          onChange={(e) => updateQuizQuestion(qIndex, "question", e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          placeholder="Digite a pergunta"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {question.options.map((option, oIndex) => (
                          <div key={oIndex} className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => updateQuizOption(qIndex, oIndex, e.target.value)}
                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                              placeholder={`Opção ${oIndex + 1}`}
                              required
                            />
                            {question.options.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removeQuizOption(qIndex, oIndex)}
                                className="text-red-500 hover:text-red-700"
                                title="Remover opção"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => addQuizOption(qIndex)}
                        className="mt-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs"
                      >
                        Adicionar opção
                      </button>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Resposta Correta
                        </label>
                        <select
                          value={question.correct_answer}
                          onChange={(e) =>
                            updateQuizQuestion(qIndex, "correct_answer", parseInt(e.target.value))
                          }
                          className="px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        >
                          {question.options.map((option, oIndex) => (
                            <option key={oIndex} value={oIndex}>
                              Opção {oIndex + 1}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-medium transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Salvando...</span>
                  </div>
                ) : course ? (
                  "Atualizar Curso"
                ) : (
                  "Criar Curso"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CourseForm;
